<template>
  <v-container fluid class="admin-dashboard">
    <!-- Welcome Header Section -->
    <v-card class="welcome-card mb-6 mb-md-8 elevation-4">
      <div class="welcome-gradient"></div>
      <v-card-text class="pa-6 pa-md-8 position-relative">
        <v-row align="center">
          <v-col cols="12" md="8">
            <div class="d-flex align-center mb-3">
              <v-avatar color="primary" size="56" class="mr-4 welcome-avatar">
                <v-icon size="32" color="white">mdi-view-dashboard</v-icon>
              </v-avatar>
              <div>
                <h1 class="welcome-title">Bienvenido de vuelta a ¡Guanentino, Responda!</h1>
                <p class="welcome-subtitle mb-0">
                  Aquí tienes un resumen del estado actual del sistema
                </p>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <div class="date-chip">
              <v-chip
                variant="flat"
                size="large"
                prepend-icon="mdi-calendar-today"
                style="background-color: #0a192f"
              >
                {{ currentDate }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Stats Grid -->
    <v-row class="stats-grid mb-6">
      <v-col cols="12" sm="6" md="4">
        <v-card class="stats-card stats-card-users elevation-3">
          <div class="stats-overlay"></div>
          <v-card-text class="pa-5 pa-md-6 position-relative">
            <div class="stats-header">
              <div class="stats-icon-wrapper">
                <div class="stats-icon-container users">
                  <v-icon size="28">mdi-account-group</v-icon>
                </div>
              </div>
              <v-menu>
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-dots-vertical"
                    variant="text"
                    size="small"
                    class="stats-menu"
                  />
                </template>
                <v-list density="compact">
                  <v-list-item @click="$router.push('/admin/users')" prepend-icon="mdi-eye">
                    Ver detalles
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="stats-content mt-4">
              <div class="stats-number">{{ totalUsers.toLocaleString() }}</div>
              <div class="stats-label">Total Usuarios</div>
              <v-progress-linear
                :model-value="100"
                color="blue"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card class="stats-card stats-card-questions elevation-3">
          <div class="stats-overlay"></div>
          <v-card-text class="pa-5 pa-md-6 position-relative">
            <div class="stats-header">
              <div class="stats-icon-wrapper">
                <div class="stats-icon-container courses">
                  <v-icon size="28">mdi-book-multiple</v-icon>
                </div>
              </div>
            </div>
            <div class="stats-content mt-4">
              <div class="stats-number">{{ activeQuestions.toLocaleString() }}</div>
              <div class="stats-label">Preguntas Activas</div>
              <v-progress-linear
                :model-value="100"
                color="green"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card class="stats-card stats-card-grades elevation-3">
          <div class="stats-overlay"></div>
          <v-card-text class="pa-5 pa-md-6 position-relative">
            <div class="stats-header">
              <div class="stats-icon-wrapper">
                <div class="stats-icon-container evaluations">
                  <v-icon size="28">mdi-clipboard-check</v-icon>
                </div>
              </div>
            </div>
            <div class="stats-content mt-4">
              <div class="stats-number">{{ totalGrades.toLocaleString() }}</div>
              <div class="stats-label">Total de Grados</div>
              <v-progress-linear
                :model-value="100"
                color="amber"
                height="4"
                rounded
                class="mt-3"
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts and Quick Actions -->
    <v-row>
      <v-col cols="12" lg="8">
        <v-row>
          <!-- Pie Chart - Questions by Subject -->
          <v-col cols="12" md="6">
            <v-card class="chart-card elevation-3">
              <v-card-title class="card-header d-flex align-center">
                <v-avatar size="40" class="mr-3" style="background-color: #0a192f">
                  <v-icon color="white" size="20">mdi-chart-pie</v-icon>
                </v-avatar>
                <div>
                  <div class="card-title-text">Distribución por Materia</div>
                  <div class="card-subtitle-text">Desglose por materia</div>
                </div>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="chart-content">
                <div v-if="questionsBySubject.length > 0" class="chart-container">
                  <canvas ref="pieChartRef"></canvas>
                </div>
                <div v-else class="empty-state">
                  <v-avatar color="grey-lighten-3" size="80" class="mb-4">
                    <v-icon size="40" color="grey-lighten-1">mdi-chart-pie</v-icon>
                  </v-avatar>
                  <p class="text-body-2 text-medium-emphasis font-weight-medium">
                    No hay datos disponibles
                  </p>
                  <p class="text-caption text-medium-emphasis">
                    Agrega preguntas para ver las estadísticas
                  </p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Stacked Bar Chart - Questions by Grade and Subject -->
          <v-col cols="12" md="6">
            <v-card class="chart-card elevation-3">
              <v-card-title class="card-header d-flex align-center">
                <v-avatar size="40" class="mr-3" style="background-color: #0a192f">
                  <v-icon color="white" size="20">mdi-chart-bar-stacked</v-icon>
                </v-avatar>
                <div>
                  <div class="card-title-text">Preguntas por Grado</div>
                  <div class="card-subtitle-text">Desglose por grado</div>
                </div>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="chart-content">
                <div v-if="questionsByGradeAndSubject.datasets.length > 0" class="chart-container">
                  <canvas ref="barChartRef"></canvas>
                </div>
                <div v-else class="empty-state">
                  <v-avatar color="grey-lighten-3" size="80" class="mb-4">
                    <v-icon size="40" color="grey-lighten-1">mdi-chart-bar</v-icon>
                  </v-avatar>
                  <p class="text-body-2 text-medium-emphasis font-weight-medium">
                    No hay datos disponibles
                  </p>
                  <p class="text-caption text-medium-emphasis">
                    Agrega preguntas para ver las estadísticas
                  </p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- Quick Actions Sidebar -->
      <v-col cols="12" lg="4">
        <v-card class="quick-actions-card elevation-3">
          <v-card-title class="card-header d-flex align-center">
            <v-avatar size="40" class="mr-3" style="background-color: #0a192f">
              <v-icon color="white" size="20">mdi-lightning-bolt</v-icon>
            </v-avatar>
            <div>
              <div class="card-title-text">Acciones Rápidas</div>
              <div class="card-subtitle-text">Accesos directos</div>
            </div>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="quick-actions pa-4 pa-md-5">
            <v-card
              v-for="(action, index) in quickActions"
              :key="action.title"
              class="quick-action-card mb-3 elevation-0"
              :class="`quick-action-${index}`"
              @click="$router.push(action.to)"
            >
              <v-card-text class="pa-4">
                <div class="d-flex align-center">
                  <v-avatar :color="action.color" size="48" class="mr-4">
                    <v-icon :icon="action.icon" size="24" color="white"></v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="action-title">{{ action.title }}</div>
                    <div class="action-subtitle">{{ action.description }}</div>
                  </div>
                  <v-icon color="grey-lighten-1">mdi-chevron-right</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useUserStore } from '@/stores/user'
import Chart from 'chart.js/auto'

const store = useStore()
const userStore = useUserStore()

const pieChartRef = ref(null)
const barChartRef = ref(null)
let pieChartInstance = null
let barChartInstance = null

const subjectColors = [
  '#5B9BD5',
  '#70C1A0',
  '#F4B942',
  '#E67C7C',
  '#9B7EBD',
  '#E589B8',
  '#4FB3D9',
  '#F19A5B',
  '#7B92E0',
  '#5DBDAD',
  '#B490DB',
  '#E896C7',
]

const totalUsers = computed(() => {
  return userStore.pagination.total || 0
})

const activeQuestions = computed(() => {
  return store.getters['questions/questions']?.length || 0
})

const totalGrades = computed(() => {
  return store.getters['schoolGrades/allGrades']?.length || 0
})

const questions = computed(() => {
  return store.getters['questions/questions'] || []
})

const grades = computed(() => {
  return store.getters['schoolGrades/allGrades'] || []
})

// Process data for pie chart - Questions by Subject
const questionsBySubject = computed(() => {
  const subjectMap = new Map()

  questions.value.forEach((question) => {
    const subjectName = question.school_subject?.name || 'Sin asignatura'
    const count = subjectMap.get(subjectName) || 0
    subjectMap.set(subjectName, count + 1)
  })

  return Array.from(subjectMap.entries()).map(([name, count]) => ({
    name,
    count,
  }))
})

// Process data for stacked bar chart - Questions by Grade and Subject
const questionsByGradeAndSubject = computed(() => {
  if (grades.value.length === 0 || questions.value.length === 0) {
    return { labels: [], datasets: [] }
  }

  // Get all unique subjects
  const subjectsSet = new Set()
  questions.value.forEach((question) => {
    const subjectName = question.school_subject?.name || 'Sin asignatura'
    subjectsSet.add(subjectName)
  })
  const subjects = Array.from(subjectsSet)

  // Create a map for grade -> subject -> count
  const gradeSubjectMap = new Map()

  questions.value.forEach((question) => {
    const gradeId = question.school_grade_id
    const subjectName = question.school_subject?.name || 'Sin asignatura'

    if (!gradeSubjectMap.has(gradeId)) {
      gradeSubjectMap.set(gradeId, new Map())
    }

    const subjectMap = gradeSubjectMap.get(gradeId)
    subjectMap.set(subjectName, (subjectMap.get(subjectName) || 0) + 1)
  })

  // Sort grades by grade number
  const sortedGrades = [...grades.value].sort((a, b) => a.grade - b.grade)

  // Create labels (grade names)
  const labels = sortedGrades.map((grade) => `Grado ${grade.grade}`)

  // Create datasets (one per subject)
  const datasets = subjects.map((subject, index) => ({
    label: subject,
    data: sortedGrades.map((grade) => {
      const subjectMap = gradeSubjectMap.get(grade.id)
      return subjectMap ? subjectMap.get(subject) || 0 : 0
    }),
    backgroundColor: subjectColors[index % subjectColors.length],
    borderColor: subjectColors[index % subjectColors.length],
    borderWidth: 1,
  }))

  return { labels, datasets }
})

const initPieChart = () => {
  if (!pieChartRef.value || questionsBySubject.value.length === 0) return

  const ctx = pieChartRef.value.getContext('2d')

  if (pieChartInstance) {
    pieChartInstance.data.labels = questionsBySubject.value.map((item) => item.name)
    pieChartInstance.data.datasets[0].data = questionsBySubject.value.map((item) => item.count)
    pieChartInstance.data.datasets[0].backgroundColor = subjectColors.slice(
      0,
      questionsBySubject.value.length,
    )
    pieChartInstance.update()
    return
  }

  pieChartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: questionsBySubject.value.map((item) => item.name),
      datasets: [
        {
          data: questionsBySubject.value.map((item) => item.count),
          backgroundColor: subjectColors.slice(0, questionsBySubject.value.length),
          borderColor: '#ffffff',
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1.5,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 12,
            },
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = context.label || ''
              const value = context.parsed || 0
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${label}: ${value} (${percentage}%)`
            },
          },
        },
      },
    },
  })
}

const initBarChart = () => {
  if (!barChartRef.value || questionsByGradeAndSubject.value.datasets.length === 0) return

  const ctx = barChartRef.value.getContext('2d')

  if (barChartInstance) {
    barChartInstance.data.labels = questionsByGradeAndSubject.value.labels
    barChartInstance.data.datasets = questionsByGradeAndSubject.value.datasets
    barChartInstance.update()
    return
  }

  barChartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: questionsByGradeAndSubject.value.labels,
      datasets: questionsByGradeAndSubject.value.datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1.5,
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            stepSize: 1,
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 12,
            },
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
      },
    },
  })
}

const handleResize = () => {
  if (pieChartInstance) {
    pieChartInstance.resize()
  }
  if (barChartInstance) {
    barChartInstance.resize()
  }
}

onMounted(async () => {
  await Promise.all([
    store.dispatch('questions/fetchQuestions'),
    store.dispatch('schoolGrades/fetchGrades'),
    store.dispatch('schoolSubjects/fetchAllSubjects'),
    userStore.fetchUsers(),
  ])

  // Wait for DOM update and initialize charts
  await nextTick()
  initPieChart()
  initBarChart()

  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  if (pieChartInstance) {
    pieChartInstance.destroy()
    pieChartInstance = null
  }
  if (barChartInstance) {
    barChartInstance.destroy()
    barChartInstance = null
  }
  window.removeEventListener('resize', handleResize)
})

// Current date
const currentDate = computed(() => {
  const date = new Date()
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
})

const quickActions = ref([
  {
    title: 'Gestionar Reportes',
    description: 'Ver estadísticas y reportes',
    icon: 'mdi-chart-line',
    color: '#70C1A0',
    to: '/reports',
  },
  {
    title: 'Gestionar Usuarios',
    description: 'Ver y administrar usuarios',
    icon: 'mdi-account-group',
    color: '#F19A5B',
    to: '/admin/users',
  },
])
</script>

<style scoped>
/* Container */
.admin-dashboard {
  max-width: 1600px;
  margin: 0 auto;
  padding: 16px;
}

@media (min-width: 960px) {
  .admin-dashboard {
    padding: 24px;
  }
}

/* Welcome Card */
.welcome-card {
  border-radius: 20px !important;
  overflow: hidden;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideDown 0.6s ease-out;
}

.welcome-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.3) !important;
}

.welcome-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.welcome-avatar {
  animation: pulse 2s ease-in-out infinite;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.welcome-title {
  color: #0a192f;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-subtitle {
  color: #0a192f;
  font-size: 0.95rem;
  font-weight: 400;
}

.date-chip {
  animation: fadeIn 0.8s ease-out 0.3s both;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Stats Grid */
.stats-grid {
  animation: fadeInUp 0.6s ease-out 0.2s both;
}

.stats-card {
  border-radius: 18px !important;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  overflow: hidden;
  position: relative;
}

.stats-overlay {
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  opacity: 0.05;
}

.stats-card-users .stats-overlay {
  background: #5b9bd5;
}

.stats-card-questions .stats-overlay {
  background: #70c1a0;
}

.stats-card-grades .stats-overlay {
  background: #f19a5b;
}

.stats-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(0, 0, 0, 0.08);
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stats-icon-container {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stats-icon-container.users {
  background: #5b9bd5;
}

.stats-icon-container.courses {
  background: #70c1a0;
}

.stats-icon-container.evaluations {
  background: #f19a5b;
}

.stats-menu {
  color: #94a3b8 !important;
}

.stats-content {
  animation: fadeIn 0.5s ease-out 0.2s both;
}

.stats-number {
  font-size: 2.25rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin-bottom: 8px;
  background: #0a192f;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stats-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Chart Cards */
.chart-card {
  border-radius: 18px !important;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  height: 100%;
}

.card-header {
  padding: 20px 24px !important;
  background: rgba(0, 0, 0, 0.01);
}

.card-title-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.card-subtitle-text {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
  margin-top: 2px;
}

.chart-content {
  padding: 24px !important;
  min-height: 320px;
}

.chart-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s ease-out;
}

.chart-container canvas {
  max-width: 100%;
  height: auto !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 24px;
  animation: fadeIn 0.5s ease-out;
}

/* Quick Actions */
.quick-actions-card {
  border-radius: 18px !important;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  height: 100%;
}

.quick-actions-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.quick-action-card {
  border-radius: 14px !important;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.quick-action-card:hover {
  transform: translateX(8px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1) !important;
  border-color: rgba(0, 0, 0, 0.1);
}

.quick-action-card:active {
  transform: translateX(4px) scale(0.98);
}

.action-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2px;
}

.action-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 1279px) {
  .chart-content {
    min-height: 300px;
  }
}

@media (max-width: 959px) {
  .admin-dashboard {
    padding: 16px;
  }

  .welcome-title {
    font-size: 1.35rem;
  }

  .welcome-subtitle {
    font-size: 0.9rem;
  }

  .stats-number {
    font-size: 2rem;
  }

  .stats-icon-container {
    width: 50px;
    height: 50px;
  }
}

@media (max-width: 599px) {
  .admin-dashboard {
    padding: 12px;
  }

  .welcome-card {
    border-radius: 16px !important;
  }

  .welcome-title {
    font-size: 1.2rem;
  }

  .welcome-subtitle {
    font-size: 0.85rem;
  }

  .welcome-avatar {
    width: 48px !important;
    height: 48px !important;
  }

  .date-chip {
    margin-top: 12px;
  }

  .stats-card,
  .chart-card,
  .quick-actions-card {
    border-radius: 14px !important;
  }

  .stats-number {
    font-size: 1.75rem;
  }

  .stats-icon-container {
    width: 44px;
    height: 44px;
  }

  .card-title-text {
    font-size: 1rem;
  }

  .chart-content {
    padding: 16px !important;
    min-height: 260px;
  }

  .quick-action-card {
    margin-bottom: 12px !important;
  }
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}
</style>
